// 車両台帳ビュー（中身/TMS・純マスタ／登録専用）。
// 収支サマリーからは切り離した「車両の登録名簿」。列＝車番/車両名/車種/日額/車検/ETC/状態/備考。
// vehicles コレクションを直読み。＋新規登録・実データ同期（未登録の実在車番を取込）・状態(稼働/休車)。
// 収支サマリー（運行実績×収支）は本台帳の「日額・車種」を参照して車両経費を計算＝この連携だけ残す（実績値は台帳に載せない）。
// デモ（Supabase未接続）時は同梱サンプルから同期プレビュー（保存は本番のみ）。
import { computeOperations, VEHICLE_DAY_RATE } from '../data/analytics/operations.js'
import { COLLECTIONS, listDocs, upsert } from '../data/collections.js'
import { mapVehicles, planVehicles } from '../data/ingest/masters.js'
import { decodeBytes, readTable } from '../data/ingest/csv.js'
import { supabaseReady } from '../supabase.js'
import sampleOps from './sample.ops.json'
import { allowSample } from '../data/demoData.js'

const YEN = (n) => '¥' + Math.round(Number(n) || 0).toLocaleString('ja-JP')
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const normPlate = (v) => String(v == null ? '' : v).trim().replace(/\.0+$/, '').replace(/^0+(?=\d)/, '')

const V = { el: null, ctx: {}, online: false, fetchError: false, masters: {}, rows: [], draft: null, sortKey: 'carNo', sortDir: 'asc', saving: '', busy: '', importPlan: null }

function styleOnce() {
  if (document.getElementById('veh-style')) return
  const s = document.createElement('style'); s.id = 'veh-style'
  s.textContent = `
  .vh{display:flex;flex-direction:column;gap:12px}
  .vh-bar{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  .vh-btn{font-family:inherit;font-size:12.5px;font-weight:700;color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:7px 13px;cursor:pointer}
  .vh-btn:hover{background:var(--head)}
  .vh-btn.primary{background:var(--accent-fill);color:#fff;border-color:var(--accent-fill)}
  .vh-btn.primary:hover{background:var(--accent-fill)}
  .vh-btn:disabled{opacity:.5;cursor:default}
  .vh-demo{font-size:11px;color:var(--signal);background:var(--card);border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .vh-save{font-size:11px;color:var(--pos);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:3px 10px}
  .vh-open{margin-left:auto;font-size:12px;color:var(--text);text-decoration:none;background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:6px 12px}
  .vh-kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px}
  .vh-kpi{background:var(--card);border:1px solid var(--e1);border-radius:10px;padding:11px 13px}
  .vh-kpi .k{font-size:10.5px;color:var(--muted)}.vh-kpi .v{font-size:19px;font-weight:700;font-variant-numeric:tabular-nums;margin-top:2px}
  .vh-kpi.acc .v{color:var(--text)}
  .vh-tblwrap{border:1px solid var(--e1);border-radius:12px;overflow:hidden;background:var(--card)}
  .vh-tblwrap .cap{padding:9px 13px;font-size:12px;font-weight:700;color:var(--muted);background:var(--head);border-bottom:1px solid var(--e1);display:flex;justify-content:space-between;align-items:center}
  .vh-scroll{max-height:58vh;overflow:auto}
  table.vh-tbl{width:100%;border-collapse:collapse;font-size:12px}
  .vh-tbl th,.vh-tbl td{padding:5px 10px;border-bottom:1px solid var(--e1);text-align:left;white-space:nowrap}
  .vh-tbl th{position:sticky;top:0;background:var(--head);color:var(--text);font-size:10.5px;z-index:1;cursor:pointer;user-select:none}
  .vh-tbl th:hover{background:var(--head);color:var(--text)}
  .vh-tbl td.n,.vh-tbl th.n{text-align:right}
  .vh-tbl select,.vh-tbl input{font-family:inherit;font-size:12px;border:1px solid var(--e1);border-radius:6px;padding:3px 6px;background:var(--card);color:var(--text)}
  .vh-tbl input.num{width:82px;text-align:right;font-variant-numeric:tabular-nums}
  .vh-tbl input.w-name{width:150px}.vh-tbl input.w-cn{width:78px}.vh-tbl input.w-sm{width:88px}.vh-tbl input.w-memo{width:150px}
  .vh-tbl tr.off td{background:var(--card)}
  .vh-tbl tr.off input,.vh-tbl tr.off select{color:var(--text)}
  .vh-tbl tr.draft td{background:var(--card)}
  .vh-cn{font-variant-numeric:tabular-nums;font-weight:700}
  .vh-note{font-size:11px;color:var(--muted);line-height:1.6}
  .vh-modal{position:fixed;inset:0;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;z-index:50;padding:20px}
  .vh-card{background:var(--card);border-radius:14px;width:min(720px,96vw);max-height:88vh;display:flex;flex-direction:column;border:1px solid var(--e1);box-shadow:inset 0 1px 0 var(--hilite);overflow:hidden}
  .vh-card-h{padding:13px 16px;font-size:14px;font-weight:800;color:var(--text);border-bottom:1px solid var(--e1);display:flex;gap:10px;align-items:baseline}
  .vh-card-sum{padding:11px 16px;font-size:12.5px;color:var(--text);background:var(--card);border-bottom:1px solid var(--e1);line-height:1.65}
  .vh-card-scroll{overflow:auto;padding:2px 8px}
  .vh-card-f{padding:12px 16px;display:flex;gap:10px;justify-content:flex-end;border-top:1px solid var(--e1)}
  .vh-bnew{color:var(--pos);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700}
  .vh-benr{color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px;font-weight:700}
  .vh-bskip{color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:999px;padding:2px 8px;font-size:11px}
  `
  document.head.appendChild(s)
}

function optFromMasters() {
  const vehRates = {}, vehClass = {}
  for (const id in V.masters) { const m = V.masters[id]; if (m.dailyCost != null && m.dailyCost !== '') vehRates[id] = Number(m.dailyCost); if (m.klass) vehClass[id] = m.klass }
  return { vehRates, vehClass }
}

function buildRows() {
  V.rows = Object.values(V.masters).map((m) => ({
    id: m.id, carNo: m.carNo || m.id, name: m.name || '', klass: m.klass || 'medium',
    dailyCost: (m.dailyCost != null ? m.dailyCost : VEHICLE_DAY_RATE[m.klass || 'medium']),
    inspection: m.inspection || '', etc: m.etc || '', status: m.status || 'active', note: m.note || '',
  }))
}

const COLS = [['carNo', '車番'], ['name', '車両名'], ['klass', '車種'], ['dailyCost', '日額(円)'], ['inspection', '車検'], ['etc', 'ETC'], ['status', '状態'], ['note', '備考']]

function sortRows(rows) {
  const k = V.sortKey, mul = V.sortDir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    let x = a[k], y = b[k]
    if (k === 'dailyCost') return ((Number(x) || 0) - (Number(y) || 0)) * mul
    if (k === 'carNo') { const nx = Number(x), ny = Number(y); if (!isNaN(nx) && !isNaN(ny)) return (nx - ny) * mul }
    return String(x).localeCompare(String(y), 'ja') * mul
  })
}

function rowInputs(r, draft) {
  const idAttr = draft ? 'data-draft="1"' : `data-id="${esc(r.id)}"`
  const cn = draft
    ? `<input class="w-cn" data-f="carNo" value="${esc(r.carNo)}" placeholder="車番" ${idAttr}>`
    : `<span class="vh-cn">${esc(r.carNo)}</span>`
  return `
    <td>${cn}</td>
    <td><input class="w-name" data-f="name" value="${esc(r.name)}" ${idAttr}></td>
    <td><select data-f="klass" ${idAttr}><option value="large"${r.klass === 'large' ? ' selected' : ''}>大型</option><option value="medium"${r.klass === 'medium' ? ' selected' : ''}>中型</option></select></td>
    <td class="n"><input class="num" type="number" step="500" min="0" data-f="dailyCost" value="${esc(r.dailyCost)}" ${idAttr}></td>
    <td><input class="w-sm" data-f="inspection" value="${esc(r.inspection)}" placeholder="YYYY-MM" ${idAttr}></td>
    <td><input class="w-sm" data-f="etc" value="${esc(r.etc)}" ${idAttr}></td>
    <td><select data-f="status" ${idAttr}><option value="active"${r.status !== 'inactive' ? ' selected' : ''}>稼働</option><option value="inactive"${r.status === 'inactive' ? ' selected' : ''}>休車</option></select></td>
    <td><input class="w-memo" data-f="note" value="${esc(r.note)}" ${idAttr}></td>`
}

function paint() {
  const el = V.el
  const rows = sortRows(V.rows)
  const large = V.rows.filter((r) => r.klass === 'large').length
  const active = V.rows.filter((r) => r.status !== 'inactive').length
  const arrow = (k) => V.sortKey === k ? (V.sortDir === 'asc' ? ' ▲' : ' ▼') : ''
  const head = COLS.map((c) => `<th class="${c[0] === 'dailyCost' ? 'n' : ''}" data-k="${c[0]}">${c[1]}${arrow(c[0])}</th>`).join('') + '<th></th>'
  const draftRow = V.draft ? `<tr class="draft">${rowInputs(V.draft, true)}<td><button class="vh-btn primary" id="vh-commit">登録</button></td></tr>` : ''
  const body = rows.map((r) => `<tr class="${r.status === 'inactive' ? 'off' : ''}">${rowInputs(r, false)}<td></td></tr>`).join('')
  const empty = (!rows.length && !V.draft)
    ? '<div class="ct-state is-empty"><b class="ct-state__t">まだ車両が登録されていません</b><span>「実データから取込」で運行データに出てくる実在車両を一括登録できます。／「＋新規車両」で1台ずつ追加もできます。</span></div>'
    : ''
  el.innerHTML = `
  <section class="vh">
    <div class="vh-bar">
      <button class="vh-btn primary" id="vh-add">＋新規車両</button>
      <button class="vh-btn" id="vh-sync" ${V.busy ? 'disabled' : ''}>${V.busy || '実データから取込'}</button>
      <button class="vh-btn" id="vh-import" ${V.busy ? 'disabled' : ''}>CSVから取込</button>
      <input type="file" id="vh-csv" accept=".csv,.txt" style="display:none">
      ${V.online ? (V.saving ? `<span class="vh-save">${esc(V.saving)}</span>` : '') : '<span class="vh-demo">デモ表示 · 保存は本番のみ</span>'}
      <a class="vh-open" href="#/operations">運行実績×収支へ →</a>
    </div>
    <div class="vh-kpis">
      <div class="vh-kpi acc"><div class="k">登録台数</div><div class="v">${V.rows.length}台</div></div>
      <div class="vh-kpi"><div class="k">稼働 / 休車</div><div class="v">${active} / ${V.rows.length - active}</div></div>
      <div class="vh-kpi"><div class="k">大型 / 中型</div><div class="v">${large} / ${V.rows.length - large}</div></div>
      <div class="vh-kpi"><div class="k">既定日額</div><div class="v" style="font-size:13px">大型${YEN(VEHICLE_DAY_RATE.large)}<br>中型${YEN(VEHICLE_DAY_RATE.medium)}</div></div>
    </div>
    <div class="vh-tblwrap">
      <div class="cap"><span>車両台帳（登録名簿・${V.rows.length}台）</span><span class="vh-note">日額・車種は収支の車両経費に反映</span></div>
      ${empty || `<div class="vh-scroll"><table class="vh-tbl"><thead><tr>${head}</tr></thead><tbody>${draftRow}${body}</tbody></table></div>`}
    </div>
    <div class="vh-note">
      この台帳は「車両の登録簿」です（収支サマリーとは独立）。稼働日数・売上・収支などの実績は「運行実績×収支」で確認します。
      ここで設定した<b>車種・日額</b>だけが収支の車両経費計算に参照されます。車両を減らすときは<b>状態＝休車</b>にしてください（履歴保持のため物理削除はしません）。
    </div>
  ${V.importPlan ? renderImport() : ''}
  </section>`

  el.querySelector('#vh-add').addEventListener('click', () => {
    if (!V.draft) V.draft = { carNo: '', name: '', klass: 'medium', dailyCost: VEHICLE_DAY_RATE.medium, inspection: '', etc: '', status: 'active', note: '' }
    paint()
  })
  el.querySelector('#vh-sync').addEventListener('click', syncFromData)
  const impBtn = el.querySelector('#vh-import'), csvInp = el.querySelector('#vh-csv')
  if (impBtn && csvInp) {
    impBtn.addEventListener('click', () => csvInp.click())
    csvInp.addEventListener('change', (e) => { const f = e.target.files && e.target.files[0]; if (f) onCsvFile(f); e.target.value = '' })
  }
  const impOk = el.querySelector('#vh-imp-ok'); if (impOk) impOk.addEventListener('click', applyImport)
  const impCancel = el.querySelector('#vh-imp-cancel'); if (impCancel) impCancel.addEventListener('click', () => { V.importPlan = null; paint() })
  const commit = el.querySelector('#vh-commit')
  if (commit) commit.addEventListener('click', commitDraft)
  el.querySelectorAll('.vh-tbl th').forEach((th) => th.addEventListener('click', () => {
    const k = th.dataset.k; if (!k) return
    if (V.sortKey === k) V.sortDir = V.sortDir === 'asc' ? 'desc' : 'asc'; else { V.sortKey = k; V.sortDir = 'asc' }
    paint()
  }))
  // 既存行の編集
  el.querySelectorAll('[data-id]').forEach((inp) => inp.addEventListener('change', () => {
    const id = inp.dataset.id, f = inp.dataset.f
    let v = inp.value; if (f === 'dailyCost') v = v === '' ? '' : Number(v)
    saveMaster(id, { [f]: v })
  }))
  // ドラフト行の編集（メモリのみ、登録で確定）
  el.querySelectorAll('[data-draft]').forEach((inp) => inp.addEventListener('input', () => {
    const f = inp.dataset.f; let v = inp.value; if (f === 'dailyCost') v = v === '' ? '' : Number(v)
    if (V.draft) V.draft[f] = v
    if (f === 'klass' && V.draft && (V.draft.dailyCost === '' || V.draft.dailyCost == null)) V.draft.dailyCost = VEHICLE_DAY_RATE[v]
  }))
}

async function saveMaster(id, patch) {
  const cur = V.masters[id] || { id }
  V.masters[id] = { ...cur, ...patch }
  buildRows()
  if (V.online) {
    try {
      V.saving = '保存中…'; paint()
      const m = V.masters[id]
      await upsert(COLLECTIONS.vehicles, id, { carNo: m.carNo || id, name: m.name || '', klass: m.klass || 'medium', dailyCost: (m.dailyCost === '' || m.dailyCost == null) ? null : Number(m.dailyCost), inspection: m.inspection || '', etc: m.etc || '', status: m.status || 'active', note: m.note || '', updatedAt: new Date().toISOString() })
      V.saving = '保存しました ✓'
    } catch (e) { V.saving = '保存失敗（権限/接続）' }
    paint(); if (V.saving) setTimeout(() => { V.saving = ''; paint() }, 1600)
  } else paint()
}

async function commitDraft() {
  const d = V.draft; if (!d) return
  const id = normPlate(d.carNo)
  if (!id) { V.saving = '車番を入力してください'; paint(); setTimeout(() => { V.saving = ''; paint() }, 1600); return }
  if (V.masters[id]) { V.saving = 'その車番は登録済みです'; paint(); setTimeout(() => { V.saving = ''; paint() }, 1800); return }
  V.draft = null
  await saveMaster(id, { carNo: id, name: d.name, klass: d.klass, dailyCost: d.dailyCost, inspection: d.inspection, etc: d.etc, status: d.status, note: d.note })
}

// 実データ（trips×tachographs）に実在する車番のうち、未登録のものを台帳へ一括登録（非破壊＝既存は上書きしない）。
async function syncFromData() {
  V.busy = '取込中…'; paint()
  try {
    let trips = sampleOps.trips, tachos = sampleOps.tachographs
    if (V.online) {
      const [tr, ta] = await Promise.all([listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.tachographs)])
      if (tr && tr.length) { trips = tr; tachos = ta || [] }
      // 実データ0件のときサンプルを取り込むと、架空の車番・乗務員が実DBへ入る。
      // サンプルを使ってよいかは共通判定 allowSample（設定 settings/display）に委ねる（2026-08-27 案C）
      else if (!allowSample(V.online)) { trips = []; tachos = [] }
    }
    const r = computeOperations(trips, tachos, optFromMasters())
    const nameOf = {}; for (const v of r.vehicles) if (v.plate) nameOf[v.plate] = v.name
    // 台帳対象＝車両経費が付く実車番 ∪ デジタコの実在車番
    const cand = {}
    for (const vm of r.vehMaster) if (vm.carNo) cand[vm.carNo] = { klass: vm.klass, name: nameOf[vm.carNo] || vm.carNo }
    for (const v of r.vehicles) if (v.plate && !cand[v.plate]) cand[v.plate] = { klass: 'medium', name: v.name || v.plate }
    let added = 0, enriched = 0
    for (const cn in cand) {
      const c = cand[cn]
      const ex = V.masters[cn]
      if (ex) {
        // 非破壊エンリッチ：車両名が空ならデジタコ名で補完（既存の編集は上書きしない）
        if ((!ex.name || ex.name === '') && c.name && c.name !== cn) {
          V.masters[cn] = { ...ex, name: c.name }
          if (V.online) await upsert(COLLECTIONS.vehicles, cn, { name: c.name, updatedAt: new Date().toISOString() })
          enriched++
        }
        continue
      }
      const rec = { id: cn, carNo: cn, name: c.name, klass: c.klass, dailyCost: VEHICLE_DAY_RATE[c.klass], inspection: '', etc: '', status: 'active', note: '' }
      V.masters[cn] = rec
      if (V.online) await upsert(COLLECTIONS.vehicles, cn, { carNo: cn, name: c.name, klass: c.klass, dailyCost: VEHICLE_DAY_RATE[c.klass], status: 'active', updatedAt: new Date().toISOString() })
      added++
    }
    buildRows(); V.busy = ''; V.saving = `新規${added}台・車両名補完${enriched}台 ✓`; paint()
    setTimeout(() => { V.saving = ''; paint() }, 2200)
  } catch (e) { V.busy = ''; V.saving = '取込に失敗しました'; paint(); setTimeout(() => { V.saving = ''; paint() }, 2200) }
}

// CSVから取込：ファイル → 文字コード自動復号 → 車両番号でマッピング → 既存照合 → プレビュー。
async function onCsvFile(file) {
  V.busy = '読込中…'; paint()
  try {
    const buf = await file.arrayBuffer()
    const text = decodeBytes(new Uint8Array(buf), 'auto')
    const { records } = readTable(text)
    const cands = mapVehicles(records)
    if (!cands.length) throw new Error('「車両番号」列が見つかりませんでした')
    const planned = planVehicles(cands, V.masters)
    const counts = { total: planned.length, new: 0, enrich: 0, skip: 0 }
    for (const p of planned) counts[p.kind]++
    V.importPlan = { fileName: file.name, planned, counts }
    V.busy = ''; paint()
  } catch (e) { V.busy = ''; V.saving = 'CSV読込に失敗: ' + (e && e.message ? e.message : e); paint(); setTimeout(() => { V.saving = ''; paint() }, 2800) }
}

function renderImport() {
  const p = V.importPlan
  const badge = (k) => k === 'new' ? '<span class="vh-bnew">新規</span>' : k === 'enrich' ? '<span class="vh-benr">補完</span>' : '<span class="vh-bskip">変更なし</span>'
  const body = p.planned.map((r) => `<tr><td class="vh-cn">${esc(r.carNo)}</td><td>${esc(r.name || '—')}</td><td>${esc(r.depot || '')}</td><td>${badge(r.kind)}</td></tr>`).join('')
  return `
  <div class="vh-modal" id="vh-modal">
    <div class="vh-card">
      <div class="vh-card-h">CSVから取込プレビュー<span class="vh-note">${esc(p.fileName)}</span></div>
      <div class="vh-card-sum">
        全${p.counts.total}件 ／ <b>新規 ${p.counts.new}台</b>・情報補完 ${p.counts.enrich}台・変更なし ${p.counts.skip}台
        <div class="vh-note">車番＝CSVの「車両番号」。車種・日額はCSVに無いため取込対象外です（新規は中型・既定日額で登録＝取込後に各車で設定してください）。既存車の車種・日額・状態・備考は上書きしません（空欄の車両名のみ補完）。</div>
      </div>
      <div class="vh-card-scroll"><table class="vh-tbl"><thead><tr><th>車番</th><th>車両名</th><th>所属</th><th>判定</th></tr></thead><tbody>${body}</tbody></table></div>
      <div class="vh-card-f">
        <button class="vh-btn" id="vh-imp-cancel">キャンセル</button>
        <button class="vh-btn primary" id="vh-imp-ok">この内容で取込（新規${p.counts.new}・補完${p.counts.enrich}）</button>
      </div>
    </div>
  </div>`
}

async function applyImport() {
  const p = V.importPlan; if (!p) return
  V.importPlan = null; V.busy = '取込中…'; paint()
  let added = 0, enriched = 0
  try {
    for (const r of p.planned) {
      if (r.kind === 'new') {
        const klass = r.klass || 'medium'
        const rec = { id: r.id, carNo: r.carNo, name: r.name || '', klass, dailyCost: VEHICLE_DAY_RATE[klass], inspection: '', etc: '', status: 'active', note: r.depot || '', depot: r.depot || '' }
        V.masters[r.id] = rec
        if (V.online) await upsert(COLLECTIONS.vehicles, r.id, { carNo: rec.carNo, name: rec.name, klass: rec.klass, dailyCost: rec.dailyCost, status: 'active', note: rec.note, depot: rec.depot, updatedAt: new Date().toISOString() })
        added++
      } else if (r.kind === 'enrich') {
        const ex = V.masters[r.id]; if (!ex) continue
        V.masters[r.id] = { ...ex, ...r.patch }
        if (V.online) await upsert(COLLECTIONS.vehicles, r.id, { ...r.patch, updatedAt: new Date().toISOString() })
        enriched++
      }
    }
    buildRows(); V.busy = ''; V.saving = `CSV取込: 新規${added}台・補完${enriched}台 ✓`; paint()
    setTimeout(() => { V.saving = ''; paint() }, 2800)
  } catch (e) { V.busy = ''; V.saving = '取込に失敗しました（権限/接続）'; paint(); setTimeout(() => { V.saving = ''; paint() }, 2800) }
}

export async function renderVehicles(el, ctx = {}) {
  styleOnce()
  V.el = el
  V.ctx = ctx
  V.online = !ctx.demo && supabaseReady
  V.fetchError = false
  V.masters = {}; V.draft = null
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">読み込んでいます…</b><span>車両台帳を読み込んでいます</span></div>'
  if (V.online) {
    try { const vh = await listDocs(COLLECTIONS.vehicles); for (const m of (vh || [])) V.masters[m.id] = { ...m } }
    catch { V.online = false; V.fetchError = true }
  }
  // サインイン済みの実fetch失敗時は「0台の空状態」を装わず is-error を主表示にする
  // （デモ表示は明示的なデモ状況＝online初期値false のときだけ。2026-08-27 負債解消）
  if (V.fetchError) { paintFetchError(); return }
  buildRows()
  paint()
}

// 実fetch失敗（デモ意図とは別）の主表示。空の台帳を装わない
function paintFetchError() {
  V.el.innerHTML = '<section class="vh"><div class="ct-state is-error"><b class="ct-state__t">車両台帳を読み込めませんでした</b><span>保存済みの台帳データを取得できていません。接続を確認して再試行してください</span><button type="button" class="btn" id="vh-retry">再試行</button></div></section>'
  const retry = V.el.querySelector('#vh-retry'); if (retry) retry.addEventListener('click', () => { renderVehicles(V.el, V.ctx) })
}
